<script lang="ts">
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import ActionBox from "@kontextwerk/web-sdk/ui/layout/ActionBox.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { mdiSkipNext } from "@mdi/js"
  import type { OrganizationOnboardingStep } from "../../../../../../../../../../../../../../api/kehrblatt/organization-onboarding"
  import OnboardingMetricStrip from "./_modules/onboarding-metric-strip"
  import OnboardingSummaryList from "./_modules/onboarding-summary-list"
  import {
    onboardingStatusColor,
    onboardingStatusLabel,
    primaryActionLabel,
  } from "../../../../_shared/_logic/onboarding-presentation"

  let {
    step,
    isActive = false,
    isLocked = false,
    actionLoading = false,
    primaryDisabled = false,
    primaryIcon = "",
    onPrimaryAction,
    onSkip,
  }: {
    step: OrganizationOnboardingStep
    isActive?: boolean
    isLocked?: boolean
    actionLoading?: boolean
    primaryDisabled?: boolean
    primaryIcon?: string
    onPrimaryAction: () => void
    onSkip: () => void
  } = $props()

  const showPrimaryAction = $derived(!isLocked)
  const showSkipAction = $derived(isActive && step.status === "OPEN" && step.can_skip)
</script>

{#snippet summaryContent()}
  {#if step.summary}
    <div class="summary-body">
      <div class="summary-copy">
        <strong>{step.summary.headline}</strong>
        {#if step.summary.description}
          <span>{step.summary.description}</span>
        {/if}
      </div>
      <OnboardingMetricStrip metrics={step.summary.metrics} />
      <OnboardingSummaryList summary={step.summary} />
    </div>
  {/if}
{/snippet}

<div class="step-content">
  {#if showPrimaryAction}
    <div class="step-panel">
      <ActionBox
        label={step.title}
        tag={onboardingStatusLabel(step)}
        tagColor={onboardingStatusColor(step)}
        darkMode={true}
        brightBorder={true}
      >
        {#snippet buttonSnippet()}
          <div class="actions">
            <CTA
              type="primaryButton"
              content={primaryActionLabel(step)}
              disabled={actionLoading || primaryDisabled}
              darkMode={false}
              onClick={onPrimaryAction}
            >
              {#snippet iconSnippet()}
                <Icon path={primaryIcon} />
              {/snippet}
            </CTA>
            {#if showSkipAction}
              <CTA
                type="secondaryButton"
                content="Schritt überspringen"
                disabled={actionLoading}
                darkMode={false}
                onClick={onSkip}
              >
                {#snippet iconSnippet()}
                  <Icon path={mdiSkipNext} />
                {/snippet}
              </CTA>
            {/if}
          </div>
        {/snippet}

        <p>{step.description}</p>
        {@render summaryContent()}
      </ActionBox>
    </div>
  {:else}
    <div class="step-panel">
      <ActionBox
        label={step.title}
        tag={onboardingStatusLabel(step)}
        tagColor={onboardingStatusColor(step)}
        darkMode={true}
        brightBorder={true}
      >
        <p>{step.description}</p>
        {@render summaryContent()}
      </ActionBox>
    </div>
  {/if}
</div>

<style lang="less">
  .step-content {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 1rem;
    width: 100%;
    max-width: none;
    align-items: stretch;
  }

  .step-panel {
    min-width: 0;
  }

  p {
    margin: 0;
    color: color-mix(in srgb, var(--text-100) 72%, transparent);
    line-height: 1.45;
  }

  .actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.6rem;
    flex-wrap: wrap;
  }

  .summary-body {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    border-top: 1px solid color-mix(in srgb, var(--text-100) 18%, transparent);
    margin-top: 0.85rem;
    padding-top: 0.85rem;
  }

  .summary-copy {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  .summary-copy strong {
    color: var(--text-100);
    font-size: 0.98rem;
    line-height: 1.2;
  }

  .summary-copy span {
    color: color-mix(in srgb, var(--text-100) 66%, transparent);
    line-height: 1.35;
  }
</style>
